import { RefObject, useEffect } from "react";

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const canScrollVertically = (el: HTMLElement) => {
  if (el.scrollHeight <= el.clientHeight) return false;
  const { overflowY } = window.getComputedStyle(el);
  return overflowY === "auto" || overflowY === "scroll";
};

// Nearest vertically scrollable element between the touched node and the
// modal container (inclusive), or null when nothing in that chain can scroll.
const findScrollable = (from: EventTarget | null, boundary: HTMLElement) => {
  let el = from instanceof HTMLElement ? from : null;
  while (el && boundary.contains(el)) {
    if (canScrollVertically(el)) return el;
    el = el.parentElement;
  }
  return null;
};

/**
 * react-scrolllock keeps the page behind an open modal still on phones by
 * cancelling EVERY touchmove on the document (iOS Safari ignores
 * overflow:hidden on the body). Cancelling everything also kills touch
 * scrolling inside the modal itself, so tall modals such as Create Shipment
 * could not be scrolled on a phone at all.
 *
 * This lets a touchmove through when something between the finger and the
 * modal container can actually scroll; the document-level lock keeps
 * cancelling the rest. No-op on non-touch devices, so desktop is unchanged.
 */
export const useTouchScrollable = (
  ref: RefObject<HTMLElement>,
  isActive: boolean
) => {
  useEffect(() => {
    const container = ref.current;
    if (!isActive || !container || !isTouchDevice()) return;

    const onTouchStart = (e: TouchEvent) => {
      const el = findScrollable(e.target, container);
      if (!el) return;
      // Nudge off the exact top/bottom edge so iOS scrolls the element
      // instead of rubber-banding the page behind it (same trick as
      // react-scrolllock's own TouchScrollable).
      if (el.scrollTop === 0) {
        el.scrollTop = 1;
      } else if (el.scrollTop + el.clientHeight >= el.scrollHeight) {
        el.scrollTop -= 1;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (findScrollable(e.target, container)) {
        e.stopPropagation();
      }
    };

    const options: AddEventListenerOptions = { passive: false };
    container.addEventListener("touchstart", onTouchStart, options);
    container.addEventListener("touchmove", onTouchMove, options);

    return () => {
      container.removeEventListener("touchstart", onTouchStart, options);
      container.removeEventListener("touchmove", onTouchMove, options);
    };
  }, [ref, isActive]);
};
